<template>
  <div class="wizard-container">
    <!-- Header -->
    <div class="wizard-header">
      <h2 class="wizard-title">Welcome on board!</h2>
      <div class="wizard-steps">
        <span
          v-for="(s, i) in steps"
          :key="s.key"
          class="step"
          :class="{ active: currentStepIndex === i, done: i < currentStepIndex }"
          :aria-current="currentStepIndex === i ? 'step' : undefined"
        >
          {{ i + 1 }}
        </span>
      </div>
    </div>

    <!-- Body -->
    <div class="wizard-body">
      <!-- Step 0: Profile intro -->
      <section v-if="currentStepIndex === 0" class="step-pane">
        <personal-data
          ref="personalDataRef"
          :onboardingMode="true"
          :user-contact-info="userContactInfo"
          @update-data="onUpdateUserData"
        />
      </section>

      <!-- Step 1: Select activities -->
      <section v-if="currentStepIndex === 1" class="step-pane">
        <h3 class="pane-title">Select activities</h3>
        <div class="toolbar">
          <button class="link" type="button" @click="selectAll">Select all</button>
          <span aria-hidden="true">·</span>
          <button class="link" type="button" @click="clearAll">Clear</button>
        </div>
        <ul class="activity-list" role="list">
          <li v-for="act in activities" :key="act.value" class="activity-item">
            <label class="checkbox-row">
              <input
                type="checkbox"
                :value="act.value"
                :checked="selectedSet.has(act.value)"
                @change="toggle(act.value, $event.target.checked)"
              />
              <span class="activity-name">{{ act.description }}</span>
            </label>
          </li>
          <li v-if="activities.length === 0" class="empty">
            No activities provided.
          </li>
        </ul>
      </section>

      <!-- Step 2: Experience notes per activity, one page per activity -->
      <section v-else-if="currentStepIndex === 2 && !showPromptResult" class="step-pane">
        <p class="muted" v-if="selectedIds.length === 0">
          No activities selected. Go back to choose at least one.
        </p>
        <div v-else class="notes-list">
          <div class="note-card">
            <label class="note-label" :for="`note-${selectedIds[currentNoteIndex]}`">
              Explain in a free form your experience in
              <strong>{{ activityMap[selectedIds[currentNoteIndex]]?.description || selectedIds[currentNoteIndex] }}</strong>
            </label>
            <textarea
              :id="`note-${selectedIds[currentNoteIndex]}`"
              v-model="activityNotes[selectedIds[currentNoteIndex]]"
              class="note-input"
              rows="5"
              placeholder="Write anything that shows your experience, achievements, projects, outcomes, duration, tools, team size…"
            ></textarea>
          </div>
          <div class="notes-pagination" v-if="selectedIds.length > 1">
            <span>Activity {{ currentNoteIndex + 1 }} of {{ selectedIds.length }}</span>
          </div>
        </div>
      </section>

      <section v-else-if="currentStepIndex === 2 && showPromptResult" class="step-pane">
        <h3 class="pane-title">Your CV (you can always edit it in your profile)</h3>
        <div class="note-card">
          <div v-if="promptResult && promptResult.success">
            <ActivityCVView
              :json-text="promptResult.data"
              :title="activityMap[selectedIds[currentNoteIndex]]?.description || 'Your CV'"
            />
          </div>
          <div v-else>
            <em>Loading...</em>
          </div>
        </div>
      </section>

      <!-- Step 3: Review -->
      <section v-else-if="currentStepIndex === 3" class="step-pane">
        <h3 class="pane-title">Review</h3>
        <!-- <div v-if="selectedIds.length === 0" class="empty">No activities selected.</div>
        <div v-else class="review">
          <div v-for="id in selectedIds" :key="`rev-${id}`" class="review-item">
            <div class="review-title">{{ activityMap[id]?.description || id }}</div>
            <pre class="review-text">{{ (activityNotes[id] || '').trim() || '— (no text provided) —' }}</pre>
          </div>
        </div> -->
        <div> You are all set! Press Finish to complete your onboarding and start using AllTeams.</div>
      </section>
    </div>

    <!-- Footer / Controls -->
    <div class="wizard-footer">
      <button
        class="btn ghost"
        type="button"
        :disabled="currentStepIndex === 0"
        @click="prev"
      >
        Prev
      </button>
      <div class="spacer" />
      <button
        class="btn"
        type="button"
        @click="nextOrFinish"
        :disabled="
          (currentStepIndex === 2 && !showPromptResult && (
            selectedIds.length === 0 ||
            !(activityNotes[selectedIds[currentNoteIndex]] && activityNotes[selectedIds[currentNoteIndex]].trim())
          ))
        "
      >
        {{ currentStepIndex < steps.length - 1 ? 'Next' : 'Finish' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ActivityCVView from '@/components/onboarding/ActivityCVView.vue'
import PersonalData from '@/components/profile/PersonalData.vue'
import { createActivityCVFromPrompt, createCVForUser } from '@/api/ai.js'

export default {
  name: 'OnboardingWizard',
  components: {
    ActivityCVView,
    PersonalData,
  },
  props: {
  },
  data() {
    return {
      steps: [
        { key: 'profile', title: 'Please introduce yourself' },
        { key: 'select', title: 'Select activities' },
        { key: 'notes', title: 'Experience notes' },
        { key: 'review', title: 'Review' },
      ],
      currentStepIndex: 0,
      currentNoteIndex: 0,
      showPromptResult: false,
      promptResult: null,
      selectedSet: new Set(this.modelValue),
      activityNotes: { ...this.initialNotes },
      activityCVMap: {},
      modelValue: [],
      initialNotes: []
    }
  },
  computed: {
    ...mapState(['user', 'descriptions']),
    activities() {
      const arr = (this.descriptions.activityType || []).slice()
      arr.sort((a, b) => a.description.localeCompare(b.description))
      return arr
    },
    selectedIds() {
      return Array.from(this.selectedSet)
    },
    activityMap() {
      const m = {}
      for (const a of this.activities) m[a.value] = a
      return m
    },
    userContactInfo() {
      return this.$store.state.user.userContactInfo;
    },
    userId() {
      return this.$store.state.user.userId;
    },
  },
  watch: {
    modelValue(val) {
      this.selectedSet = new Set(val)
      this.ensureNotesForSelection()
    },
  },
  methods: {
    toggle(id, checked) {
      if (checked) this.selectedSet.add(id)
      else this.selectedSet.delete(id)
      this.$emit('update:modelValue', this.selectedIds)
      this.ensureNotesForSelection()
    },
    selectAll() {
      for (const a of this.activities) this.selectedSet.add(a.value)
      this.$emit('update:modelValue', this.selectedIds)
      this.ensureNotesForSelection()
    },
    clearAll() {
      this.selectedSet.clear()
      this.$emit('update:modelValue', [])
      this.ensureNotesForSelection()
    },
    ensureNotesForSelection() {
      const keep = new Set(this.selectedIds.map(String))
      for (const id of this.selectedIds) {
        if (!(id in this.activityNotes)) this.activityNotes[id] = ''
      }
      for (const key in this.activityNotes) {
        if (!keep.has(String(key))) delete this.activityNotes[key]
      }
    },
    async nextOrFinish() {
      if (this.currentStepIndex === 0) {
        this.currentStepIndex = 1
        this.currentNoteIndex = 0
        this.showPromptResult = false
        this.$refs.personalDataRef && this.$refs.personalDataRef.validateForm()
      }
      else if (this.currentStepIndex === 1) {
        // If on Select activities, go to first note or review if none selected
        if (this.selectedIds.length === 0) {
          this.currentStepIndex = 3
        } else {
          this.currentStepIndex = 2
          this.currentNoteIndex = 0
          this.showPromptResult = false
        }
      } else if (this.currentStepIndex === 2 && !this.showPromptResult) {
        // Notes step: call createActivityCVFromPrompt for current activity
        const currentId = this.selectedIds[this.currentNoteIndex]
        const currentText = this.activityNotes[currentId] ?? ''
        this.promptResult = await createActivityCVFromPrompt(currentId, currentText)
        this.showPromptResult = true
      } else if (this.currentStepIndex === 2 && this.showPromptResult) {
        // Prompt result page: advance to next activity or review
        const currentId = this.selectedIds[this.currentNoteIndex]
        this.activityCVMap[currentId] = this.promptResult.data
        this.showPromptResult = false
        if (this.currentNoteIndex < this.selectedIds.length - 1) {
          this.currentNoteIndex++
          const nextId = this.selectedIds[this.currentNoteIndex]
          if (!this.activityNotes[nextId] || !this.activityNotes[nextId].trim()) {
            this.activityNotes[nextId] = ''
          }
        } else {
          //this.makeActivityCV()
          this.currentStepIndex = 3
        }
      } else {
        // Review step: finish
        console.log("Onboarding finished, emitting finish event")
        this.makeActivityCV()
        this.finish()
      }
    },
    prev() {
      if (this.currentStepIndex === 2 && this.showPromptResult) {
        this.showPromptResult = false
      } else if (this.currentStepIndex === 1) {
        // If on Select activities, go back to Profile
        this.currentStepIndex = 0
      } else if (this.currentStepIndex > 0) {
        this.currentStepIndex--
      }
    },
    makeActivityCV() {
      const cvResult = createCVForUser(this.activityCVMap)
      this.$emit('generate-cv', cvResult)
      return cvResult
    },

    onUpdateUserData() {
      //this.$emit('update-user-data', updatedData)
      console.log("User data updated in onboarding")
    },
    finish() {
      this.$emit('finish')
    }
  },
  mounted() {
    this.ensureNotesForSelection()
  },
}
</script>

<style scoped>
/* Container with rounded corners */
.wizard-container {
  --border: 1px solid #e5e7eb;
  --radius: 16px;
  --pad: 16px;
  --gap: 12px;

  background: #fff;
  border: var(--border);
  border-radius: var(--radius);
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
  max-width: 780px;
  margin: 0 auto;
  display: grid;
  grid-template-rows: auto 1fr auto;
}

/* Header */
.wizard-header {
  padding: calc(var(--pad) + 4px) var(--pad) var(--pad);
  border-bottom: var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.wizard-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
}
.wizard-steps {
  display: flex;
  gap: 8px;
}
.step {
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  border: 1px solid #d1d5db;
  display: grid;
  place-items: center;
  font-size: 0.85rem;
  color: #6b7280;
  background: #fff;
}
.step.active {
  border-color: #2563eb;
  color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.12) inset;
}
.step.done {
  background: #ecfeff;
  border-color: #22d3ee;
  color: #0e7490;
}

/* Body */
.wizard-body { padding: var(--pad); }
.step-pane { display: grid; gap: var(--gap); }
.pane-title { margin: 0; font-size: 1rem; font-weight: 600; }

.toolbar { display: flex; align-items: center; gap: 8px; }
.toolbar .link {
  background: none; border: none; padding: 0; font: inherit;
  color: #2563eb; cursor: pointer;
}
.toolbar .link:hover { text-decoration: underline; }

.activity-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr; /* Two columns */
}
.activity-item {
  border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px 12px;
}
.checkbox-row { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.activity-name { font-weight: 500; }
.empty { color: #9ca3af; font-style: italic; }

/* Notes step */
.notes-list { display: grid; gap: 12px; }
.note-card {
  border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px;
  background: #fafafa;
}
.note-label { display: block; margin-bottom: 8px; }
.note-input {
  width: 100%; border: 1px solid #e5e7eb; border-radius: 10px;
  padding: 10px 12px; font: inherit; min-height: 120px; resize: vertical;
}
.note-input:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }

/* Review */
.review { display: grid; gap: 10px; }
.review-item { border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
.review-title { padding: 10px 12px; font-weight: 600; background: #f3f4f6; }
.review-text {
  margin: 0; padding: 10px 12px; white-space: pre-wrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  background: #fff;
}

/* Footer */
.wizard-footer {
  border-top: var(--border);
  padding: var(--pad);
  display: flex; align-items: center;
}
.spacer { flex: 1 1 auto; }
.btn {
  appearance: none;
  border: 1px solid #2563eb;
  background: #2563eb; color: white;
  padding: 10px 14px; border-radius: 12px; font-weight: 600; cursor: pointer;
}
.btn:hover { filter: brightness(0.97); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn.ghost { background: white; color: #111827; border-color: #e5e7eb; }
.btn.ghost:hover { background: #f9fafb; }
</style>
